// src/pages/Trend/components/analysis/consumer-reaction/SentimentChart.js
import React, { memo, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import './SentimentChart.css';
import { ErrorMessage } from '../../../../../components/common/UI';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

// BBC Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  PRIMARY_COLOR: '#1380A1',
  SECONDARY_COLOR: '#FAAB18',
  POSITIVE_COLOR: '#22c55e',
  NEGATIVE_COLOR: '#ef4444',
  NEUTRAL_COLOR: '#9e9e9e',
  TEXT_COLOR: '#333333',
  AXIS_COLOR: '#B0B0B0',
  BORDER_COLOR: '#E5E7EB'
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  // Calculate total for percentage
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
      minWidth: '140px'
    }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: '#999999',
        marginBottom: '10px'
      }}>
        {label}
      </div>
      {payload.map((entry, index) => {
        const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '3px 0',
              gap: '16px'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: entry.color
              }} />
              <span style={{
                fontSize: '11px',
                color: '#666666'
              }}>
                {entry.name}
              </span>
            </div>
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              fontFamily: "'DM Sans', sans-serif",
              color: BBC_STYLE.TEXT_COLOR
            }}>
              {percentage}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SentimentChart = ({ data }) => {
  const { translate } = useLanguage();
  const [activeTab, setActiveTab] = useState('primary');

  const processChartData = (data) => {
    if (!data?.sentiment_scores?.time_series) return [];

    const { labels, values } = data.sentiment_scores.time_series;
    return labels.map((label, index) => ({
      date: label,
      // Use translated keys for chart display
      [translate('trendPage.consumerReaction.sentiment.positive')]: values.positive[index],
      [translate('trendPage.consumerReaction.sentiment.negative')]: values.negative[index],
      [translate('trendPage.consumerReaction.sentiment.neutral')]: values.neutral[index]
    }));
  };

  if (!data?.primary?.sentiment_scores?.time_series) {
    return <ErrorMessage message={translate('trendPage.consumerReaction.errors.noSentimentData')} />;
  }

  const { primary, secondary } = data;
  const hasSecondaryData = secondary?.sentiment_scores?.time_series !== undefined;

  const primaryData = processChartData(primary);
  const secondaryData = hasSecondaryData ? processChartData(secondary) : null;

  const renderChart = (chartData, title, colorType = "primary") => {
    const positiveKey = translate('trendPage.consumerReaction.sentiment.positive');
    const negativeKey = translate('trendPage.consumerReaction.sentiment.negative');
    const neutralKey = translate('trendPage.consumerReaction.sentiment.neutral');

    return (
      <div className="chart-container-sentiment" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
        {title && (
          <div className="chart-header">
            <div
              className="metric-dot"
              style={{
                backgroundColor: colorType === "primary" ?
                  BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.SECONDARY_COLOR,
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                display: "inline-block",
                marginRight: "0.5rem"
              }}
            />
            <h3 className="chart-title" style={{
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#666666',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              margin: 0
            }}>{title}</h3>
          </div>
        )}
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="none"
                stroke="#F5F5F5"
              />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={70}
                interval="preserveStart"
                tick={{
                  dy: 16,
                  fill: BBC_STYLE.AXIS_COLOR,
                  fontSize: 11
                }}
                axisLine={{ stroke: '#D0D0D0', strokeWidth: 1 }}
                tickLine={false}
              />
              <YAxis
                tick={{
                  fill: BBC_STYLE.AXIS_COLOR,
                  fontSize: 11
                }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
              />
              <Area
                type="monotone"
                dataKey={positiveKey}
                stackId="1"
                stroke={BBC_STYLE.POSITIVE_COLOR}
                fill={BBC_STYLE.POSITIVE_COLOR}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey={negativeKey}
                stackId="1"
                stroke={BBC_STYLE.NEGATIVE_COLOR}
                fill={BBC_STYLE.NEGATIVE_COLOR}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey={neutralKey}
                stackId="1"
                stroke={BBC_STYLE.NEUTRAL_COLOR}
                fill={BBC_STYLE.NEUTRAL_COLOR}
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Custom horizontal legend */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1.5rem',
          marginTop: '12px',
          fontSize: '12px',
          fontFamily: BBC_STYLE.FONT_FAMILY
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: BBC_STYLE.POSITIVE_COLOR,
              borderRadius: '2px'
            }} />
            <span style={{ color: BBC_STYLE.TEXT_COLOR }}>{positiveKey}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: BBC_STYLE.NEGATIVE_COLOR,
              borderRadius: '2px'
            }} />
            <span style={{ color: BBC_STYLE.TEXT_COLOR }}>{negativeKey}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: BBC_STYLE.NEUTRAL_COLOR,
              borderRadius: '2px'
            }} />
            <span style={{ color: BBC_STYLE.TEXT_COLOR }}>{neutralKey}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sentiment-chart-container" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
      {/* Tab buttons - only show when comparison data exists */}
      {hasSecondaryData && (
        <div className="sentiment-tab-header">
          <button
            className={`sentiment-tab-btn ${activeTab === 'primary' ? 'active' : ''}`}
            onClick={() => setActiveTab('primary')}
          >
            <span className="tab-dot primary" />
            {translate('trendPage.filters.set1')}
          </button>
          <button
            className={`sentiment-tab-btn ${activeTab === 'secondary' ? 'active' : ''}`}
            onClick={() => setActiveTab('secondary')}
          >
            <span className="tab-dot secondary" />
            {translate('trendPage.filters.set2')}
          </button>
        </div>
      )}

      {/* Render single chart based on active tab */}
      {hasSecondaryData ? (
        activeTab === 'primary'
          ? renderChart(primaryData, null, "primary")
          : renderChart(secondaryData, null, "secondary")
      ) : (
        renderChart(primaryData)
      )}
    </div>
  );
};

export default memo(SentimentChart);
